import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Grid,
  Avatar,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  Zoom,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  Key as KeyIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { animated } from '@react-spring/web';
import authService from '../../services/authService';

// Interface para usuário
interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  status: 'active' | 'inactive';
  avatar?: string;
  lastLogin?: string;
}

const AnimatedTableRow = animated(TableRow);
const AnimatedCard = animated(Card);

// Dados de exemplo para usuários (em produção, colocar para buscar da API @joaofelipe)
// const mockUsers: User[] = [
//   {
//     id: 1,
//     name: 'Administrador',
//     email: 'admin@carmanager.com',
//     role: 'admin',
//     status: 'active',
//     lastLogin: '2023-06-10T14:30:00',
//   },
//   {
//     id: 2,
//     name: 'Lucca Goltzman',
//     email: 'lucca@exemplo.com',
//     role: 'user',
//     status: 'active',
//     lastLogin: '2023-06-05T09:15:00',
//   },
//   {
//     id: 3,
//     name: 'João Felipe',
//     email: 'joao@exemplo.com',
//     role: 'user',
//     status: 'active',
//     lastLogin: '2023-06-08T16:45:00',
//   },
//   {
//     id: 4,
//     name: 'Fábio Selva',
//     email: 'fabio@exemplo.com',
//     role: 'user',
//     status: 'inactive',
//     lastLogin: '2023-05-20T11:30:00',
//   },
//   {
//     id: 5,
//     name: 'Ana Pereira',
//     email: 'ana@exemplo.com',
//     role: 'admin',
//     status: 'active',
//     lastLogin: '2023-06-09T13:20:00',
//   }
// ];

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Simular carregamento de dados
    setLoading(true);


    // Em um cenário real, seria uma chamada à API @joaofelipe
    // Exemplo:
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await authService.getAllUsers();
        setUsers(response);
        setLoading(false);
      } catch (error) {
        setError('Não foi possível carregar os usuários.');
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    // Alternar para visualização em cards em dispositivos móveis
    if (isMobile) {
      setViewMode('card');
    } else {
      setViewMode('table');
    }
  }, [isMobile]);

  const handleAddUser = () => {
    navigate('/usuarios/novo');
  };

  const handleEditUser = (id: number) => {
    navigate(`/usuarios/editar/${id}`);
  };

  const handleResetPassword = async (id: number) => {
    try {
      // Em uma aplicação real, enviaria uma solicitação para redefinir a senha
      alert(`Enviar e-mail de redefinição de senha para o usuário ${id}`);

      // Chamada para a função de redefinir a senha
      const retorno = await authService.resetPassword(id);

      alert(`Senha do usuário ${id} foi redefinida com sucesso.`);
    } catch (error) {
      console.error('Erro ao tentar redefinir a senha:', error);
      alert('Ocorreu um erro ao tentar redefinir a senha. Tente novamente.');
    }
  };


  const handleViewUser = (id: number) => {
    navigate(`/usuarios/${id}`);
  };

  const handleDeleteUser = (id: number) => {
    // Em uma aplicação real, confirmariamos com o usuário e excluiríamos
    alert(`Excluir usuário ${id}`);
  };

  const handleDeleteUserWithError = (id: number) => {
    try {
      // Simulação de erro ao excluir
      if (id === 1) {
        throw new Error('Não é possível excluir o usuário administrador padrão');
      }

      // Código normal de exclusão
      alert(`Excluir usuário ${id}`);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Ocorreu um erro ao excluir o usuário');
      }
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  // Filtrar usuários com base no termo de pesquisa
  const filteredUsers = (users || []).filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  function formatDateBR(dateTimeStr: string): string {
    if (!dateTimeStr) return '';

    // Transformar em formato ISO para o JS
    const date = new Date(dateTimeStr.replace(' ', 'T'));

    // Checa se a data é válida
    if (isNaN(date.getTime())) return '';

    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  // Formatação de data para último login
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const cleanDateString = dateString.split('T')[0];
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Animação para linhas da tabela
  const rowAnimationStyle = {
    opacity: 1,
    transform: 'translateY(0)'
  };

  // Animação para cards
  const cardAnimationStyle = {
    opacity: 1,
    transform: 'scale(1)'
  };

  // Calcular usuários paginados
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Calcular número total de páginas
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Usuários
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gerenciamento de usuários do sistema
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            sx={{
              py: 1.2,
              px: 3,
              borderRadius: '8px',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            Novo Usuário
          </Button>
        </Box>

        {/* Estatísticas de usuários */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Total de Usuários
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {users ? users.length : 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {/* {(users || []).filter(u => u.status === 'active').length} ativos, {users.filter(u => u.status === 'inactive').length} inativos */}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <AdminIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Administradores
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {(users || []).filter(u => u.role === 'admin').length}

              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Com acesso completo ao sistema
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Usuários Padrão
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {(users || []).filter(u => u.role === 'user').length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Acesso limitado às funcionalidades
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.grey[700] }}>
                  <ViewIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Visualizadores
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {(users || []).filter(u => u.role === 'viewer').length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Apenas visualização (sem edição/cadastro)
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtros e pesquisa */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Buscar por nome ou e-mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {filteredUsers.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '12px' }}>
            <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhum usuário encontrado</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '500px', mx: 'auto' }}>
              Não foram encontrados usuários com os filtros atuais. Tente modificar sua busca ou cadastre um novo usuário.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              sx={{
                py: 1.2,
                borderRadius: '8px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
              }}
            >
              Novo Usuário
            </Button>
          </Paper>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                mb: 3
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }}>
                    <TableCell>Nome</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Papel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Último Login</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user, index) => (
                    <AnimatedTableRow
                      key={user.id}
                      hover
                      style={{
                        ...rowAnimationStyle,
                        transition: `all 0.3s ease ${index * 0.1}s`
                      }}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(63, 136, 197, 0.04)',
                          cursor: 'pointer',
                        }
                      }}
                      onClick={() => handleViewUser(user.id)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              bgcolor: user.role === 'admin' ? theme.palette.primary.main : theme.palette.grey[500],
                              width: 36,
                              height: 36,
                              mr: 2
                            }}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography>{user.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography>{user.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                          label={user.role === 'admin' ? 'Administrador' : user.role === 'viewer' ? 'Visualizador' : 'Usuário'}
                          color={user.role === 'admin' ? 'primary' : 'default'}
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status === 'active' ? 'Ativo' : 'Inativo'}
                          color={user.status === 'active' ? 'success' : 'error'}
                          size="small"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>{formatDateBR(user.lastLogin ?? '')}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="Mais opções" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, user.id)}
                              sx={{
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'rotate(90deg)' }
                              }}
                            >
                              <MoreIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </AnimatedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedUsers.map((user, index) => (
                <Grid item xs={12} sm={6} md={4} key={user.id}>
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                    <Box>
                      <AnimatedCard
                        style={{
                          ...cardAnimationStyle,
                          transition: `all 0.3s ease ${index * 0.1}s`
                        }}
                        sx={{
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                          }
                        }}
                        onClick={() => handleViewUser(user.id)}
                      >
                        <Box
                          sx={{
                            bgcolor: user.role === 'admin' ? theme.palette.primary.main : theme.palette.grey[600],
                            color: 'white',
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 70,
                              height: 70,
                              mb: 1.5,
                              bgcolor: 'white',
                              color: user.role === 'admin' ? theme.palette.primary.main : theme.palette.grey[700],
                              boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                            }}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2">
                            {user.email}
                          </Typography>
                          <Chip
                            label={user.role === 'admin' ? 'Administrador' : user.role === 'viewer' ? 'Visualizador' : 'Usuário'}
                            size="small"
                            sx={{
                              mt: 1,
                              fontWeight: 'medium',
                              bgcolor: 'rgba(255,255,255,0.15)',
                              color: 'white'
                            }}
                          />
                        </Box>

                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Status:
                            </Typography>
                            <Chip
                              label={user.status === 'active' ? 'Ativo' : 'Inativo'}
                              color={user.status === 'active' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 'medium' }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <MailIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                            <Typography variant="body2" noWrap>
                              {user.email}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <KeyIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Último acesso: {formatDateBR(user.lastLogin ?? '')}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }} onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditUser(user.id);
                            }}
                          >
                            Editar
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, user.id)}
                          >
                            <MoreIcon />
                          </IconButton>
                        </CardActions>
                      </AnimatedCard>
                    </Box>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        )}

        {/* Menu de ações */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <MenuItem onClick={() => {
            if (selectedUserId) handleViewUser(selectedUserId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver Detalhes</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedUserId) handleEditUser(selectedUserId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedUserId) handleResetPassword(selectedUserId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Redefinir Senha</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedUserId) handleDeleteUserWithError(selectedUserId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </Fade>
  );
};

export default UserList; 